/**
 * FinancingTab.tsx
 *
 * Patient-detail financing section matching legacy patient-financing.php.
 * Displays financing application records from patients_financing_match.
 * Account-gated: only shown for accountIDs 1,2,4,5,6,7,8.
 */
import { useQuery } from '@tanstack/react-query';
import { getPatientFinancing, type FinancingRecord } from '@/services/financing.service';
import { Spinner } from '@/components/ui';

function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface FinancingTabProps {
  patientId: number;
}

export function FinancingTab({ patientId }: FinancingTabProps) {
  const { data: records, isLoading, isError } = useQuery({
    queryKey: ['patient-financing', patientId],
    queryFn: () => getPatientFinancing(patientId),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  if (isError) {
    return <p className="py-4 text-sm text-red-600">Failed to load financing information.</p>;
  }

  const rows = records ?? [];

  if (rows.length === 0) {
    return (
      <div className="space-y-4 py-2">
        <h2 className="text-lg font-semibold text-slate-900">Financing Information</h2>
        <p className="py-4 text-sm text-slate-400 italic">No financing records found for this patient.</p>
      </div>
    );
  }

  const grouped = new Map<string, FinancingRecord[]>();
  for (const r of rows) {
    const key = r.provider ?? 'General';
    const arr = grouped.get(key) ?? [];
    arr.push(r);
    grouped.set(key, arr);
  }

  return (
    <div className="space-y-6 py-2">
      <h2 className="text-lg font-semibold text-slate-900">Financing Information</h2>

      {Array.from(grouped.entries()).map(([provider, items]) => (
        <div key={provider} className="space-y-2">
          <h3 className="text-sm font-medium text-slate-600 uppercase tracking-wide">{provider}</h3>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Question</th>
                  <th className="px-4 py-3">Response</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => (
                  <tr key={item.patientsFinancingMatchID} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">{item.provider ?? `Field #${item.patientsFinancingID}`}</td>
                    <td className="px-4 py-3 text-slate-800">{item.response || '—'}</td>
                    <td className="px-4 py-3 text-slate-500">{formatShortDate(item.added)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
